/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  assetsPdfService,
  liabilitiesPdfService,
  assetCommitmentSchedulePdfService
) => {
  /**
   * Prepare assets and liabilities details.
   *
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} liabilities
   * @param {Array} homeLoanSecurities
   * @param {Array} assetCommitmentSchedules
   * @param {Array} lenders
   * @param {Object} workflow
   *
   * @returns {Array}
   */
  const prepareAssetsAndLiabilitiesDetails = ({
    contacts,
    assets,
    liabilities,
    homeLoanSecurities,
    assetCommitmentSchedules,
    lenders,
    workflow
  }) => {
    const content = [];
    const len = contacts?.length || 0;
    for (let c = 0; c < len; c += 1) {
      const contact = contacts[c];
      const contactAssets = _.filter(
        assets,
        (a) =>
          helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            a.ownership,
            contact.id
          ) > 0
      );
      if (contactAssets?.length) {
        content.push(
          helperPdfService.createHeader({
            title: `ASSETS - ${contact.getName().toUpperCase()}`,
            pageBreak: 'before'
          })
        );
        let ownerOccupierPropertyAddressCount = 0;
        let investmentPropertyAddressCount = 0;
        let vehicleMakeAndModelCount = 0;
        let bankAccountsCount = 0;
        let homeContentsCount = 0;
        let kiwiSaverCount = 0;
        let superfundInstitutionCount = 0;
        let sharesCount = 0;
        let otherCount = 0;
        let balanceSheetCount = 0;
        const aLen = contactAssets.length;
        for (let a = 0; a < aLen; a += 1) {
          const asset = contactAssets[a];
          if (helperService.isAssetTypeOwnerOccupied(asset.assetType)) {
            ownerOccupierPropertyAddressCount += 1;
            content.push(
              assetsPdfService.prepareOwnerOccupiedAssetDetails({
                asset,
                contacts,
                homeLoanSecurities,
                workflow,
                count: ownerOccupierPropertyAddressCount
              })
            );
          }
          if (helperService.isAssetTypeInvestment(asset.assetType)) {
            investmentPropertyAddressCount += 1;
            content.push(
              assetsPdfService.prepareInvestmentAssetDetails({
                asset,
                contacts,
                homeLoanSecurities,
                workflow,
                count: investmentPropertyAddressCount
              })
            );
          }
          if (helperService.isAssetTypeVehicle(asset.assetType)) {
            vehicleMakeAndModelCount += 1;
            content.push(
              assetsPdfService.prepareVehicleAssetDetails({
                asset,
                contacts,
                homeLoanSecurities,
                count: vehicleMakeAndModelCount
              })
            );
          }
          if (helperService.isAssetTypeBankAccount(asset.assetType)) {
            bankAccountsCount += 1;
            content.push(
              assetsPdfService.prepareBankAccountAssetDetails({
                asset,
                contacts,
                lenders,
                count: bankAccountsCount
              })
            );
          }
          if (helperService.isAssetTypeHomeContents(asset.assetType)) {
            homeContentsCount += 1;
            content.push(
              assetsPdfService.prepareHomeContentsAssetDetails({
                asset,
                contacts,
                count: homeContentsCount
              })
            );
          }
          if (helperService.isAssetTypeKiwiSaver(asset.assetType)) {
            kiwiSaverCount += 1;
            content.push(
              assetsPdfService.prepareKiwiSaverAssetDetails({
                asset,
                contacts,
                count: kiwiSaverCount
              })
            );
          }
          if (helperService.isAssetTypeSuperfund(asset.assetType)) {
            superfundInstitutionCount += 1;
            content.push(
              assetsPdfService.prepareSuperfundAssetDetails({
                asset,
                contacts,
                count: superfundInstitutionCount
              })
            );
          }
          if (helperService.isAssetTypeShares(asset.assetType)) {
            sharesCount += 1;
            content.push(
              assetsPdfService.prepareSharesAssetDetails({
                asset,
                contacts,
                count: sharesCount
              })
            );
          }
          if (helperService.isAssetTypeOther(asset.assetType)) {
            otherCount += 1;
            content.push(
              assetsPdfService.prepareOtherAssetDetails({
                asset,
                contacts,
                count: otherCount
              })
            );
          }
          if (helperService.isAssetTypeBalanceSheet(asset.assetType)) {
            balanceSheetCount += 1;
            content.push(
              assetsPdfService.prepareBalanceSheetAssetDetails({
                asset,
                contacts,
                count: balanceSheetCount
              })
            );
          }
        }
      }
      const contactLiabilities = _.filter(
        liabilities,
        (l) =>
          helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            l.ownership,
            contact.id
          ) > 0
      );
      if (contactLiabilities?.length) {
        if (contactAssets?.length) {
          content.push({
            text: '',
            margin: [0, 10, 0, 0]
          });
        }
        content.push(
          helperPdfService.createHeader({
            title: `LIABILITIES - ${contact.getName().toUpperCase()}`,
            pageBreak: !contactAssets?.length ? 'before' : ''
          })
        );
        let mortgageLoanCount = 0;
        let creditCardCount = 0;
        let overdraftCount = 0;
        let vehicleLoanCount = 0;
        let personalLoanCount = 0;
        let smsfLoanCount = 0;
        let educationDebtCount = 0;
        let otherCount = 0;
        let balanceSheetCount = 0;
        const lLen = contactLiabilities.length;
        for (let l = 0; l < lLen; l += 1) {
          const liability = contactLiabilities[l];
          if (helperService.isLiabilityTypeMortgage(liability.liabilityType)) {
            mortgageLoanCount += 1;
            content.push(
              liabilitiesPdfService.prepareMortgageLiabilityDetails({
                liability,
                contacts,
                assets,
                lenders,
                count: mortgageLoanCount
              })
            );
          }
          if (helperService.isLiabilityTypeCreditCard(liability.liabilityType)) {
            creditCardCount += 1;
            content.push(
              liabilitiesPdfService.prepareCreditCardLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: creditCardCount
              })
            );
          }
          if (helperService.isLiabilityTypeOverdraft(liability.liabilityType)) {
            overdraftCount += 1;
            content.push(
              liabilitiesPdfService.prepareOverdraftLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: overdraftCount
              })
            );
          }
          if (helperService.isLiabilityTypeVehicle(liability.liabilityType)) {
            vehicleLoanCount += 1;
            content.push(
              liabilitiesPdfService.prepareVehicleLiabilityDetails({
                liability,
                contacts,
                assets,
                lenders,
                count: vehicleLoanCount
              })
            );
          }
          if (helperService.isLiabilityTypePersonal(liability.liabilityType)) {
            personalLoanCount += 1;
            content.push(
              liabilitiesPdfService.preparePersonalLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: personalLoanCount
              })
            );
          }
          if (helperService.isLiabilityTypeSmsf(liability.liabilityType)) {
            smsfLoanCount += 1;
            content.push(
              liabilitiesPdfService.prepareSmsfLiabilityDetails({
                liability,
                contacts,
                assets,
                lenders,
                count: smsfLoanCount
              })
            );
          }
          if (helperService.isLiabilityTypeEducationDebt(liability.liabilityType)) {
            educationDebtCount += 1;
            content.push(
              liabilitiesPdfService.prepareEducationDebtLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: educationDebtCount
              })
            );
          }
          if (helperService.isLiabilityTypeOther(liability.liabilityType)) {
            otherCount += 1;
            content.push(
              liabilitiesPdfService.prepareOtherLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: otherCount
              })
            );
          }
          if (helperService.isLiabilityTypeBalanceSheet(liability.liabilityType)) {
            balanceSheetCount += 1;
            content.push(
              liabilitiesPdfService.prepareOtherLiabilityDetails({
                liability,
                contacts,
                lenders,
                count: balanceSheetCount
              })
            );
          }
        }
      }

      if (workflow.isAssetFinance() && contact.isCompany) {
        const contactAssetCommitments = _.find(
          assetCommitmentSchedules,
          (a) => a?.idContact === contact?.id
        );
        content.push(
          assetCommitmentSchedulePdfService.prepareAssetCommitmentScheduleDetails({
            assetCommitments: contactAssetCommitments?.assetCommitments || [],
            lenders,
            contact,
            isPageBreak: contactAssets?.length || contactLiabilities?.length
          })
        );
      }
    }
    return content;
  };

  return {
    prepareAssetsAndLiabilitiesDetails
  };
};
